import {
    DoppeActionCTAButton,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    DoppeActionCTATypeLinkAutoSource,
    DoppeActionCTATypeLinkOpenType,
    DoppeActionSettingsCTAButtonSettings
} from './doppe-action-cta-button';
import {
    assertDefined,
    autoValueCreate,
    evaluateFunction,
    HideableValue,
    PartiallyOptional
} from '@wix/devzai-utils-common';
import {DoppeCheckoutProvider} from './doppe-checkout-provider';
import type {DoppeCheckoutActionCheckoutSettings} from './doppe-checkout-action-settings';

export interface DoppeActionCtaButtonSettings {
    ctaButton: DoppeActionCTAButton.SingleButton;
}

export function getDefaultDoppeActionCtaButtonSettings(options: {
    buttonText?: HideableValue<string>,
    url?: string,
    showButton: boolean
    settings?: DoppeActionSettingsCTAButtonSettings
}): DoppeActionCtaButtonSettings {

    const {
        buttonText,
        url,
        showButton,
        settings
    } = options;


    return {
        ctaButton: {
            settings: settings ?? {
                type: DoppeActionCTAType.Link,
                url: url !== undefined ? url : autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
                openType: DoppeActionCTATypeLinkOpenType.SameTab
            },
            text: buttonText ? buttonText : '',
            showButton: showButton,
        }
    }
}

export function doppeActionCtaMultipleButtonExtendWithDefaultValues (
    options: {
        ctaButton: PartiallyOptional<DoppeActionCTAButton.MultipleButton, 'isVisible' | 'isRequired'>,
        hasSourceURL: boolean
    }
) {
    const {
        ctaButton,
        hasSourceURL
    } = options;

    const {
        isVisible,
        isRequired,
        ...restCtaButtonData
    } = assertDefined(doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
        ctaButton: ctaButton,
        hasSourceURL: hasSourceURL
    })).ctaButton;

    return {
        isVisible: isVisible ?? true,
        isRequired: isRequired ?? false,
        ...restCtaButtonData,
    };
}

export function doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration<T extends DoppeActionCTAButton.BaseType>(
    options: {
        ctaButton?: T,
        hasSourceURL: boolean
    }
) {

    const {
        ctaButton,
        hasSourceURL
    } = options;

    if (!ctaButton) {
        return undefined;
    }

    const defaultSettings = doppeActionCtaButtonTypeGetDefaultSettings(ctaButton.settings.type, hasSourceURL);

    const settings = evaluateFunction(() => {
        switch (ctaButton.settings.type) {
            case DoppeActionCTAType.Checkout: {

                const {
                    checkoutSettings,
                    ...restSettings
                } = ctaButton.settings;

                const {
                    checkoutSettings: defaultCheckoutSettings,
                    ...restDefaultSettings
                } = defaultSettings as DoppeActionSettingsCTAButtonSettings.Checkout;

                const mergedCheckoutSettings = evaluateFunction((): DoppeCheckoutActionCheckoutSettings => {
                    if (!checkoutSettings || !('kind' in checkoutSettings)) {
                        return defaultCheckoutSettings;
                    }

                    switch (checkoutSettings.kind) {
                        case DoppeCheckoutProvider.Stripe: {
                            const defaultStripeInnerSettings =
                                (defaultCheckoutSettings as DoppeCheckoutActionCheckoutSettings.Stripe).settings;
                            return {
                                kind: DoppeCheckoutProvider.Stripe,
                                settings: {
                                    ...defaultStripeInnerSettings,
                                    ...checkoutSettings.settings,
                                },
                            };
                        }
                        case DoppeCheckoutProvider.WixEcom: {
                            return {
                                kind: DoppeCheckoutProvider.WixEcom,
                                settings: {
                                    ...checkoutSettings.settings,
                                },
                            };
                        }
                    }
                });

                return {
                    ...restDefaultSettings,
                    checkoutSettings: mergedCheckoutSettings,
                    ...restSettings,
                } as DoppeActionSettingsCTAButtonSettings.Checkout;
            }
            case DoppeActionCTAType.Whatsapp: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.Whatsapp;
            }
            case DoppeActionCTAType.Call: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.Call;
            }
            case DoppeActionCTAType.Share: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,

                } as DoppeActionSettingsCTAButtonSettings.Share;
            }
            case DoppeActionCTAType.Link: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.Link;
            }
            case DoppeActionCTAType.IsracardPay: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.IsracardPay;
            }
            case DoppeActionCTAType.OpenBlogPost:
            case DoppeActionCTAType.OpenProduct:
            case DoppeActionCTAType.OpenService:
            case DoppeActionCTAType.OpenEvent:
            case DoppeActionCTAType.OpenPricingPlan: {
                // Migrate deprecated Open* button types to Link with "Same as source URL"
                const openSettings = ctaButton.settings as DoppeActionSettingsCTAButtonSettings.OpenProduct;
                return {
                    type: DoppeActionCTAType.Link,
                    url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
                    openType: openSettings.openType ?? DoppeActionCTATypeLinkOpenType.SameTab,
                } as DoppeActionSettingsCTAButtonSettings.Link;
            }
            case DoppeActionCTAType.OpenPage: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.OpenPage;
            }
            case DoppeActionCTAType.Email: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.Email;
            }
            case DoppeActionCTAType.Navigate: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.Navigate;
            }
            case DoppeActionCTAType.NavigateV2: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.NavigateV2;
            }
            case DoppeActionCTAType.FacebookMessage: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.FacebookMessage;
            }
            case DoppeActionCTAType.InstagramMessage: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.InstagramMessage;
            }
            case DoppeActionCTAType.SaveContact: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.SaveContact;
            }
            case DoppeActionCTAType.WixEcomCheckout: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.WixEcomCheckout;
            }
            case DoppeActionCTAType.FormSubmit: {
                return {
                    ...defaultSettings,
                    ...ctaButton.settings,
                } as DoppeActionSettingsCTAButtonSettings.FormSubmit;
            }

        }
    })

    return {
        ctaButton: {
            ...ctaButton,
            settings: settings
        } as T
    }
}

/**
 * Migrates OpenProduct, OpenBlogPost, OpenService, OpenEvent, OpenPricingPlan CTA button settings
 * to Link button settings with "Same as source URL".
 * 
 * @param settings - The CTA button settings to migrate
 * @returns The migrated settings (Link with autoValue) or the original settings if no migration needed
 */
export function migrateOpenEntityButtonToLinkButton(
    settings: DoppeActionSettingsCTAButtonSettings
): DoppeActionSettingsCTAButtonSettings {
    switch (settings.type) {
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
            return {
                type: DoppeActionCTAType.Link,
                url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
                openType: (settings as DoppeActionSettingsCTAButtonSettings.OpenProduct).openType ?? DoppeActionCTATypeLinkOpenType.SameTab,
            } as DoppeActionSettingsCTAButtonSettings.Link;
        default:
            return settings;
    }
}


